import { effect, inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CustomerModel } from '@libs/account/models';
// eslint-disable-next-line no-restricted-imports -- Auth state configures the checkout feature flag from customer features.
import { FeatureFlagsService } from '@libs/shared/common';
import { AuthToken, CheckAuthStateResult, ConnectionAuthModel, ConnectionAuthProvider } from '@libs/shared/models';
import { signalStore, withComputed, withHooks, withMethods, withProps } from '@ngrx/signals';
import { combineLatest, exhaustMap, filter, firstValueFrom, from, map, Observable } from 'rxjs';
import { UserAuthStateModel } from '../auth.model';
import { CustomersState } from './customer-auth-state';
import { AuthTokenState } from './token-auth-state';

@Injectable({ providedIn: 'root' })
export class AuthState extends signalStore(
  withProps(() => ({
    authTokenState: inject(AuthTokenState),
    customersState: inject(CustomersState),
    featureFlagService: inject(FeatureFlagsService),
  })),
  withComputed(store => ({
    isLoading: (): boolean => {
      return store.authTokenState.loading() || store.customersState.loading();
    },
    connectionAuth: (): ConnectionAuthModel => ({
      token: store.authTokenState.token(),
      customerNumber: store.customersState.currentCustomer()?.id,
    }),
  })),
  withProps(store => ({
    _isLoading$: toObservable(store.isLoading),
    _isTokenLoading$: toObservable(store.authTokenState.loading),
    isCurrentCustomerBlocked: store.customersState.isCurrentCustomerBlocked,
    currentCustomer: store.customersState.currentCustomer,
    allCustomers: store.customersState.customers,
    customersLoaded: store.customersState.customersLoaded,
    getAllCustomers: store.customersState.getAllCustomers,
    getCurrentCustomer: store.customersState.getCurrentCustomer,
  })),
  withMethods(store => ({
    loadCustomersIfNotLoaded: (forceReload: boolean = false): Promise<void> => {
      return store.customersState.customersLoaded() && !forceReload
        ? Promise.resolve()
        : store.customersState.loadCustomers();
    },
  })),
  withMethods(store => ({
    // Implementation of ConnectionAuthProvider
    getAuthRaw: (): ConnectionAuthModel => {
      return store.connectionAuth();
    },
    getAuth: (): Observable<ConnectionAuthModel> => {
      return store._isLoading$.pipe(
        filter(isLoading => !isLoading),
        exhaustMap(() => combineLatest({
          token: store.authTokenState.readToken(),
          customerNumber: store.customersState.getCurrentCustomer().pipe(
            map(customer => customer?.id),
          ),
        })),
      );
    },
    getTokenBasedAuth: (): Observable<ConnectionAuthModel> => {
      return store._isTokenLoading$.pipe(
        filter(isLoading => !isLoading),
        exhaustMap(() => combineLatest({
          token: store.authTokenState.readToken(),
        })),
      );
    },
    // -------------
    agentSetUser: async (userId: string): Promise<void> => {
      return store.authTokenState.agentSetUser(userId)
        .then(() => {
          return store.loadCustomersIfNotLoaded(true);
        });
    },
    login: async (newToken?: AuthToken): Promise<void> => {
      return store.authTokenState.login(newToken)
        .then(() => {
          return store.loadCustomersIfNotLoaded();
        });
    },
    register: async (): Promise<void> => {
      return store.authTokenState.register()
        .then(() => {
          return store.loadCustomersIfNotLoaded();
        });
    },
    loginLlDirectoryUser: async (newToken: AuthToken): Promise<void> => {
      if (!newToken.roles?.includes('LekkerlandAgent')) {
        return Promise.reject(new Error('missing permissions'));
      }

      return store.authTokenState.login(newToken);
    },
    loginSingleCustomer: async (customerNumber: string): Promise<void> => {
      return store.authTokenState.login()
        .then(() => {
          return store.customersState.loadCustomerByNumber(customerNumber);
        });
    },
    loadCustomers: async (): Promise<void> => {
      const currentCustomer: CustomerModel | undefined = store.currentCustomer();

      return store.customersState.loadCustomers()
        .then(() => currentCustomer ? store.customersState.changeCustomer(currentCustomer) : Promise.resolve());
    },
    clearCustomers: (): void => {
      return void store.customersState.clear();
    },
    logout: async (): Promise<void> => {
      store.customersState.clear();

      return store.authTokenState.logout();
    },
  })),
  withMethods(store => ({
    readUserToken: (): Observable<AuthToken | undefined> => {
      return store.authTokenState.readToken();
    },
    loginUser: (newToken?: AuthToken): Observable<void> => {
      return from(store.authTokenState.login(newToken));
    },
    // only for use in storybook
    setAuthRaw: async (authToken: AuthToken, customers: CustomerModel[]): Promise<void> => {
      await store.authTokenState.updateToken(authToken);
      store.customersState.setCustomersRaw(customers);
    },
  })),
  withMethods(store => ({
    readUserAuthState: (): Observable<UserAuthStateModel> => {
      return combineLatest([store.readUserToken(), store.customersState.getCurrentCustomer()]).pipe(map(([userToken, customer]) => ({
        user: !!userToken && !!customer,
        agent: userToken?.type === 'llDirectoryUser',
      })));
    },
    checkAuthState: async (): Promise<CheckAuthStateResult> => {
      const token: AuthToken | undefined = await firstValueFrom(store.readUserToken());

      if (!token) {
        return 'notLoggedIn';
      }

      if (token.type === 'llDirectoryUser' && !token.userId) {
        return 'agentNoUserId';
      }

      await store.loadCustomersIfNotLoaded();

      return 'success';
    },
    changeCurrentCustomer: async (customer: CustomerModel): Promise<void> => {
      await store.customersState.changeCustomer(customer);
    },
  })),
  withHooks(store => ({
    onInit(): void {
      effect(() => {
        if (!store.authTokenState.token() && !store.isLoading() && store.customersState.customersLoaded()) {
          void store.logout();
        }
      });
    },
  })),
) implements ConnectionAuthProvider {
}
